import Icons from "@/assets/icons";
import Button from "@/components/Button";
import Input from "@/components/Input";
import Select from "@/components/SearchableSelect";
import Textarea from "@/components/Textarea";
import { ApiService } from "@/services/ApiService";
import { messageService } from "@/services/MessageService";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import RegisterLicencaSchema from "./Schema/RegisterLicencaSchema";
import { useAuth } from "@/contexts/Auth";
import { validateFormikFields } from "@/lib/utils";
import ResourceTableSelect from "@/components/ResourceTableSelect";
import { checkHasPermission } from "@/lib/permissions";

interface Option {
  value: number;
  title: string;
}

interface OptionTipo {
  value: string;
  title: string;
}

interface BackendRecurso {
  id: number;
  valorRecurso: number;
}

const StatusBadge = ({ status, t }: { status: string; t: any }) => {
  const colors: Record<string, string> = {
    AGUARDANDO_ATIVACAO: "bg-blue-100 text-blue-800 border-blue-200",
    GERANDO_AGENTE_CLI: "bg-gray-100 text-gray-800 border-gray-200",
    ATIVA: "bg-green-100 text-green-800 border-green-200",
    FALHA_GERACAO: "bg-red-100 text-red-800 border-red-200",
    VENCIDA: "bg-red-100 text-red-800 border-red-200",
  };

  const colorClass = colors[status] || "bg-gray-100 text-gray-800";
  const label = t(`table.licenca.status.${status}`) || status;

  return (
    <span className={`px-4 py-1.5 rounded-full text-sm font-bold border ${colorClass} inline-block`}>
      {label}
    </span>
  );
};

const RegisterLicenca = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { post, patch, get } = ApiService;
  const navigate = useNavigate();
  const { usuario } = useAuth();

  const [clienteOptions, setClienteOptions] = useState<any[]>([]);
  const [produtoOptions, setProdutoOptions] = useState<Option[]>([]);
  const [recursosOptions, setRecursosOptions] = useState<Option[]>([]);
  const [apiData, setApiData] = useState<any>(null);
  const [schema] = useState(RegisterLicencaSchema(t));

  const tipoLicencaOptions: OptionTipo[] = [
    { value: "SAAS", title: "SaaS" },
    { value: "ON_PREMISE", title: "On Premise" },
  ];

  const initialValues = {
    clienteId: "",
    produtoId: "",
    recursos: [] as BackendRecurso[],
    tipoLicenca: "",
    dataDeExpiracao: "",
    emailNotificacaoInterna: "",
    observacaoLicenca: "",
  };

  const formik = useFormik({
    initialValues,
    validate: (values) => validateFormikFields(values, schema),
    onSubmit: async (values) => {
        const finalValues: any = {
          ...values,
          cadastradorPorId: usuario.id,
        };

        if (id && apiData) {
          const isLockedAbsolut = apiData.status === "GERANDO_AGENTE_CLI";
          const isLockedStructure = isLockedAbsolut || apiData.status !== "AGUARDANDO_ATIVACAO";
          const isSaas = values.tipoLicenca === "SAAS";
          const isLockedSaasFields = isLockedAbsolut || (!isSaas && isLockedStructure);

          if (isLockedStructure) {
            delete finalValues.clienteId;
            delete finalValues.produtoId;
            delete finalValues.tipoLicenca;
          }

          if (isLockedSaasFields) {
            delete finalValues.dataDeExpiracao;
            delete finalValues.recursos;
          }
        }

        const request = id
          ? patch(`api/v1/licenca/atualizar/${id}`, finalValues)
          : post("api/v1/licenca/registrar", finalValues);

        const { status } = await request;

        if (status === 200) {
          const message = id
            ? {
                title: t("LicenceForm.messages.update.title"),
                detail: t("LicenceForm.messages.update.description"),
              }
            : {
                title: t("LicenceForm.messages.save.title"),
                detail: t("LicenceForm.messages.save.description"),
              };
          messageService.success(message.title, message.detail, () => navigate("/"));
        }
    },
  });

  function formatCnpj(cnpj: string) {
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  }

  function formatUuid(uuid: string) {
    if (!uuid || uuid.length < 10) return uuid;
    return `${uuid.slice(0, 5)} *** ${uuid.slice(-5)}`;
  }

  useEffect(() => {
    const fetchSelectOptions = async () => {
      try {
        const [clientesRes, produtosRes, recursosRes] = await Promise.all([
          get("api/v1/cliente/listar"),
          get("api/v1/produto/listar"),
          get("api/v1/recurso/listar"),
        ]);

        setClienteOptions(
          clientesRes.data.map((c: any) => ({
            value: c.id,
            title: `${c.nomeFantasia} — (${formatCnpj(c.cnpj)})`,
            display: (
                <span>
                    {c.nomeFantasia} <span className="italic text-gray-500 font-normal">— ({formatCnpj(c.cnpj)})</span>
                </span>
            ),
            label: c.nomeFantasia,
            cnpj: c.cnpj,
          }))
        );

        setProdutoOptions(produtosRes.data.map((p: any) => ({ value: p.id, title: p.nomeProduto })));
        setRecursosOptions(recursosRes.data.map((r: any) => ({ value: r.id, title: r.nomeRecurso })));
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
      }
    };

    fetchSelectOptions();
  }, [get]);

  useEffect(() => {
    const fetchData = async () => {
      const hasPermission = checkHasPermission(usuario?.permissoes, "ROLE_VISUALIZAR_METADADOS");
      const endpointUrl = hasPermission
        ? `api/v1/licenca/buscarPorIdComMetadados/${id}`
        : `api/v1/licenca/buscarPorId/${id}`;

      try {
        const { data } = await get(endpointUrl);
        setApiData(data);
      } catch {
        messageService.error("Não foi possível carregar os detalhes da licença.");
      }
    };

    if (id && usuario) fetchData();
  }, [id, get, usuario]);

  useEffect(() => {
    if (apiData) {
      const formattedDate = apiData.dataDeExpiracao ? apiData.dataDeExpiracao.slice(0, 16) : "";
      formik.setValues({
        clienteId: apiData.cliente?.id ?? "",
        produtoId: apiData.produto?.id ?? "",
        tipoLicenca: apiData.tipoLicenca ?? "",
        recursos:
          apiData.recursos?.map((r: any) => ({
            id: r.id,
            valorRecurso: r.valorRecurso,
          })) ?? [],
        dataDeExpiracao: formattedDate,
        emailNotificacaoInterna: apiData.emailNotificacaoInterna ?? "",
        observacaoLicenca: apiData.observacaoLicenca ?? "",
      });
    }
  }, [apiData]);

  let formattedMetadados = "";
  if (apiData?.metadados) {
    try {
      formattedMetadados = JSON.stringify(JSON.parse(apiData.metadados), null, 2);
    } catch {
      formattedMetadados = apiData.metadados;
    }
  }

  const resourcesForComponent = recursosOptions.map((r) => ({ id: r.value, name: r.title }));

  const selectedResourcesForComponent = (() => {
    const current: BackendRecurso[] = formik.values.recursos ?? [];
    return current.map((c) => ({
      id: c.id,
      name: recursosOptions.find((opt) => opt.value === c.id)?.title ?? String(c.id),
      value: c.valorRecurso ?? 0,
    }));
  })();

  const setSelectedResourcesFromComponent = (items: any[]) => {
    const backendList: BackendRecurso[] = items.map((i) => ({
      id: i.id,
      valorRecurso: i.value,
    }));
    formik.setFieldValue("recursos", backendList, false);
  };

  const isLockedAbsolut = apiData?.status && apiData.status == "GERANDO_AGENTE_CLI";

  const isLockedStructure = isLockedAbsolut || (apiData?.status && apiData.status !== "AGUARDANDO_ATIVACAO");

  const isSaas = formik.values.tipoLicenca === "SAAS";

  const isLockedSaasFields = isLockedAbsolut || (!isSaas && isLockedStructure);

  const inputsFieldsetStyle =
    "bg-white rounded-lg border p-6 shadow-sm flex flex-col gap-6";

  const listRecusosStyle = 
    "bg-white rounded-lg border p-6 shadow-sm";
  
  const buttonsFieldsetStyle =
      "flex max-md:flex-wrap col-span-full justify-end gap-4";

  const titleFieldsetStyle = 
    "text-xl font-semibold text-gray-800 border-b pb-4 mb-4";

  return (
    <div className="flex-1 p-8 max-w-7xl mx-auto">
      <div className="mb-8 border-b pb-4">
        <h1 className="text-3xl font-medium mb-8 text-gray-800">
          {id ? t("LicenceForm.titleEdit") : t("LicenceForm.title")}
        </h1>
      </div>

      <form onSubmit={formik.handleSubmit} className="flex flex-col gap-8">
        
        <fieldset className={inputsFieldsetStyle}>
          <h2 className={titleFieldsetStyle}>{t("LicenceForm.sections.main")}</h2>

          {id && apiData?.identificador && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start mb-2">
              <div className="md:col-span-9">
                <Input 
                  id="identificador"
                  label="Identificador" 
                  value={formatUuid(apiData.identificador)}
                  disabled
                  className="font-mono bg-gray-50" 
                />
              </div>
              <div className="md:col-span-3 flex flex-col gap-1">
                <span className="block text-sm font-medium text-gray-700 mb-1">Status atual</span>
                <div>
                   <StatusBadge status={apiData.status} t={t} />
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-4">
              <Select
                id="clienteId"
                label={t("LicenceForm.inputs.clienteId.label")}
                placeholder={t("LicenceForm.inputs.clienteId.placeholder")}
                options={clienteOptions}
                disabled={isLockedStructure}
                value={formik.values.clienteId}
                onChange={(val) => formik.setFieldValue("clienteId", val)} 
                error={formik.touched.clienteId ? formik.errors.clienteId : undefined}
              />
            </div>
            <div className="md:col-span-4">
              <Select
                id="produtoId"
                label={t("LicenceForm.inputs.produtoId.label")}
                placeholder={t("LicenceForm.inputs.produtoId.placeholder")}
                options={produtoOptions}
                disabled={isLockedStructure}
                value={formik.values.produtoId}
                onChange={(val) => formik.setFieldValue("produtoId", val)} 
                error={formik.touched.produtoId ? formik.errors.produtoId : undefined}
              />
            </div>
            <div className="md:col-span-4">
              <Select
                id="tipoLicenca"
                label={t("LicenceForm.inputs.tipoLicenca.label")}
                placeholder={t("LicenceForm.inputs.tipoLicenca.placeholder")}
                options={tipoLicencaOptions}
                disabled={isLockedStructure}
                value={formik.values.tipoLicenca}
                onChange={(val) => formik.setFieldValue("tipoLicenca", val)} 
                error={formik.touched.tipoLicenca ? formik.errors.tipoLicenca : undefined}
              />
            </div>

            <div className="md:col-span-4">
              <Input
                id="dataDeExpiracao"
                type="datetime-local"
                disabled={isLockedSaasFields}
                label={t("LicenceForm.inputs.dataDeExpiracao.label")}
                {...formik.getFieldProps("dataDeExpiracao")}
                error={formik.touched.dataDeExpiracao ? formik.errors.dataDeExpiracao : undefined}
              />
            </div>
            <div className="md:col-span-8">
              <Input
                id="emailNotificacaoInterna"
                type="email"
                disabled={isLockedAbsolut}
                label={t("LicenceForm.inputs.emailNotificacaoInterna.label")}
                {...formik.getFieldProps("emailNotificacaoInterna")}
                error={formik.touched.emailNotificacaoInterna ? formik.errors.emailNotificacaoInterna : undefined}
              />
            </div>

            <div className="md:col-span-12">
              <Textarea
                rows={4}
                id="observacaoLicenca"
                disabled={isLockedAbsolut}
                label={t("LicenceForm.inputs.observacaoLicenca.label")}
                {...formik.getFieldProps("observacaoLicenca")}
                error={formik.touched.observacaoLicenca ? formik.errors.observacaoLicenca : undefined}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className={listRecusosStyle}>
          <h2 className={titleFieldsetStyle}>{t("LicenceForm.inputs.recursosIds.label")}</h2>
          <ResourceTableSelect
            resources={resourcesForComponent}
            selectedResources={selectedResourcesForComponent}
            setSelectedResources={setSelectedResourcesFromComponent}
            legend=""
            disabled={isLockedSaasFields}
          />
        </fieldset>

        {id && apiData?.metadados && (
          <fieldset className="bg-white rounded-lg border p-6 shadow-sm">
             <h2 className={titleFieldsetStyle}>{t("LicenceForm.inputs.metadados.label")}</h2>
             <Textarea 
                rows={10} 
                id="metadados" 
                value={formattedMetadados} 
                disabled
            />
          </fieldset>
        )}

        <fieldset className={buttonsFieldsetStyle}>
          <Button 
            label={t("LicenceForm.buttons.clear")} 
            color="neutro" 
            type="button" 
            onClick={() => formik.resetForm()} 
            icon={<Icons.Delete pathStyle="fill-inherit" />}
          />
          
          <Button 
            label={t("LicenceForm.buttons.cancel")} 
            color="neutro" 
            type="button" 
            onClick={() => navigate("/")} 
            icon={<Icons.Close pathStyle="fill-inherit" />}
          />

          <Button 
            label={t("LicenceForm.buttons.save")} 
            color="primario" 
            type="submit" 
            icon={<Icons.Save pathStyle="fill-inherit" />}
          />
        </fieldset>

      </form>
    </div>
  );
};

export default RegisterLicenca;